import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { AuthUserV2Response } from '@tourfold/libraries/tf-api-client-frontend-v2';

/**
 * Centralized token refresh coordinator that ensures only one refresh happens at a time
 * across all HTTP client types (HttpClient, fetch middleware, SSE).
 *
 * This service provides an Observable-based API for refresh coordination to maintain
 * proper RxJS chains and HTTP context.
 */
@Injectable({
  providedIn: 'root',
})
export class RefreshCoordinator {
  private readonly authService = inject(AuthService);

  private refreshTokenInProgress = false;
  private readonly refreshTokenSubject = new BehaviorSubject<AuthUserV2Response | string | null>(null);

  /**
   * Coordinates token refresh using Observable pattern.
   * If a refresh is already in progress, queues the request to wait.
   * Otherwise, initiates a new refresh.
   *
   * @returns Observable<AuthUserV2Response | string> The refresh response
   */
  coordinateRefresh(): Observable<AuthUserV2Response | string> {
    // If refresh already in progress, wait for result
    if (this.refreshTokenInProgress) {
      return this.refreshTokenSubject.pipe(
        filter((result): result is AuthUserV2Response | string => result !== null),
        take(1)
      );
    }

    // Start new refresh
    this.refreshTokenInProgress = true;
    this.refreshTokenSubject.next(null);

    return this.authService.refreshAccessToken().pipe(
      switchMap((refreshTokenResponse: AuthUserV2Response | string) => {
        if (
          typeof refreshTokenResponse !== 'string' &&
          refreshTokenResponse.accessToken?.validUntil &&
          refreshTokenResponse.accessToken.value &&
          refreshTokenResponse.refreshToken?.validUntil &&
          refreshTokenResponse.refreshToken.value &&
          refreshTokenResponse.id
        ) {
          // `acceptTokens` persists tokens to the token store synchronously so
          // fetch middleware retries that read from the same store immediately
          // see the fresh value, and flips loginStatus → 'success'. We skip its
          // redirect because a refresh leaves the user on whatever page they
          // were already viewing.
          this.authService.acceptTokens(refreshTokenResponse, { redirect: false });
        }

        // Reset state and notify any waiters
        this.refreshTokenInProgress = false;
        this.refreshTokenSubject.next(refreshTokenResponse);

        return of(refreshTokenResponse);
      }),
      catchError((error) => {
        // Always reset so future refresh calls are not permanently blocked.
        // Emit an error string to unblock any callers waiting on refreshTokenSubject
        // so they don't hang forever when this refresh failed.
        this.refreshTokenInProgress = false;
        this.refreshTokenSubject.next('error: token refresh failed');
        return throwError(() => error);
      })
    );
  }

  /**
   * Returns an Observable that emits when a refresh completes.
   *
   * @returns Observable<AuthUserV2Response | string | null>
   */
  getRefreshObservable(): Observable<AuthUserV2Response | string | null> {
    return this.refreshTokenSubject.asObservable();
  }

  /**
   * Checks if a refresh is currently in progress.
   *
   * @returns true if refresh is in progress, false otherwise
   */
  isRefreshInProgress(): boolean {
    return this.refreshTokenInProgress;
  }
}
